/**
 * Data summary for uploaded lab files: groups measured values by source file
 * and compares each against the authoritative limits in regulatoryStandards.ts.
 */
import { getLimit, resolveParameter } from "@/data/regulatoryStandards";
import {
  evidenceMeasurementsFromAnalysis,
  type MeasurementSource,
} from "@/lib/evidenceParser";
import type { MaterialType, QualityTier } from "@/types";

export interface SummaryInputRow {
  labReportId: string;
  reportTitle: string;
  ownerId: string;
  documentId: string | null;
  fileName: string;
  uploadedAt: string | null;
  name: string;
  cas: string;
  value: number;
  unit: string;
  nonDetect: boolean;
  source: MeasurementSource;
}

export type Verdict = "within" | "over" | "no_limit" | "not_detected";

export interface SummaryRow extends SummaryInputRow {
  standardName: string | null;
  nyLimit: number | null;
  njLimit: number | null;
  ny: Verdict;
  nj: Verdict;
  /** value / limit when over, else null. */
  nyRatio: number | null;
  njRatio: number | null;
  screening: boolean;
}

export interface FileGroup {
  key: string;
  fileName: string;
  reportTitle: string;
  labReportId: string;
  ownerId: string;
  uploadedAt: string | null;
  rows: SummaryRow[];
  overCount: number;
}

export interface SummaryFilters {
  reportId?: string | null;
  tier: QualityTier;
  material?: MaterialType;
  source?: MeasurementSource | "all";
  overOnly?: boolean;
}

const verdictOf = (value: number, limit: number | null, nd: boolean): Verdict =>
  nd ? "not_detected" : limit == null ? "no_limit" : value > limit ? "over" : "within";

export function compareRow(r: SummaryInputRow, tier: QualityTier, material: MaterialType = "general-fill"): SummaryRow {
  const ps = resolveParameter(r.name, r.cas || undefined);
  const ny = getLimit(r.name, material, tier, "NY", r.cas || undefined).limit;
  const nj = getLimit(r.name, material, tier, "NJ", r.cas || undefined).limit;
  const nyV = verdictOf(r.value, ny, r.nonDetect);
  const njV = verdictOf(r.value, nj, r.nonDetect);
  return {
    ...r,
    standardName: ps?.name ?? null,
    nyLimit: ny,
    njLimit: nj,
    ny: nyV,
    nj: njV,
    nyRatio: nyV === "over" && ny ? r.value / ny : null,
    njRatio: njV === "over" && nj ? r.value / nj : null,
    screening: ps?.basis === "guidance",
  };
}

/** Rows from analysis.evidence_measurements for reports without table rows. */
export function fallbackRows(report: { id: string; title: string; user_id: string; analysis: unknown }): SummaryInputRow[] {
  return evidenceMeasurementsFromAnalysis(report.analysis).map((m) => ({
    labReportId: report.id,
    reportTitle: report.title,
    ownerId: report.user_id,
    documentId: null,
    fileName: m.file_name || "Unnamed file",
    uploadedAt: null,
    name: m.name,
    cas: m.cas,
    value: m.value,
    unit: m.unit,
    nonDetect: m.non_detect,
    source: m.source,
  }));
}

export function buildSummary(input: SummaryInputRow[], f: SummaryFilters) {
  const rows = input
    .filter((r) => !f.reportId || r.labReportId === f.reportId)
    .filter((r) => !f.source || f.source === "all" || r.source === f.source)
    .map((r) => compareRow(r, f.tier, f.material))
    .filter((r) => !f.overOnly || r.ny === "over" || r.nj === "over");

  const groups = new Map<string, FileGroup>();
  for (const r of rows) {
    const key = `${r.labReportId}::${r.documentId ?? r.fileName}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        fileName: r.fileName,
        reportTitle: r.reportTitle,
        labReportId: r.labReportId,
        ownerId: r.ownerId,
        uploadedAt: r.uploadedAt,
        rows: [],
        overCount: 0,
      };
      groups.set(key, g);
    }
    g.rows.push(r);
    if (r.ny === "over" || r.nj === "over") g.overCount += 1;
  }

  return {
    rows,
    files: [...groups.values()],
    totals: {
      files: groups.size,
      values: rows.length,
      overNY: rows.filter((r) => r.ny === "over").length,
      overNJ: rows.filter((r) => r.nj === "over").length,
      aiToCheck: rows.filter((r) => r.source === "ai_extracted_pdf").length,
    },
  };
}

const VERDICT_TEXT: Record<Verdict, string> = {
  within: "Within",
  over: "Over",
  no_limit: "No limit",
  not_detected: "Not detected",
};

export function verdictLabel(v: Verdict, ratio: number | null): string {
  return v === "over" && ratio ? `Over by ${ratio.toFixed(ratio >= 10 ? 0 : 1)}×` : VERDICT_TEXT[v];
}

const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function summaryCsv(rows: SummaryRow[], tier: QualityTier): string {
  const header = [
    "Report", "File", "Parameter", "CAS", "Value", "Unit", "Non-detect",
    `NY limit (${tier})`, "NY result", "NJ limit", "NJ result", "Source", "Screening only",
  ];
  const lines = rows.map((r) =>
    [
      r.reportTitle, r.fileName, r.name, r.cas, r.value, r.unit, r.nonDetect ? "yes" : "no",
      r.nyLimit ?? "", verdictLabel(r.ny, r.nyRatio), r.njLimit ?? "", verdictLabel(r.nj, r.njRatio),
      r.source === "ai_extracted_pdf" ? "AI-extracted" : "Read from file", r.screening ? "yes" : "no",
    ].map(csvCell).join(","),
  );
  return [header.map(csvCell).join(","), ...lines].join("\n");
}
